import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError, prewarm, ensureDbReady } from "@/lib/api";
import { useLanding } from "@/pages/public/Landing";
import BrandMark from "@/components/BrandMark";
import { Loader2, Globe, Eye, EyeOff, CheckCircle2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { data: L } = useLanding();
  const pts = (L?.login_points || []).filter(Boolean);
  const points = pts.length ? pts : [
    "Online admissions with OTP-verified parents",
    "Fees, receipts & WhatsApp alerts",
    "Attendance, exams & printable report cards",
  ];
  const sts = (L?.login_stats || []).filter((s) => s && (s.value || s.label));
  const stats = sts.length ? sts : [
    { value: "10K+", label: "Students" },
    { value: "₹4Cr+", label: "Fees managed" },
    { value: "99.9%", label: "Uptime" },
  ];
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [slow, setSlow] = useState(false);

  // Start waking the (auto-paused) database as soon as the page loads, so that by
  // the time credentials are submitted the login is usually quick.
  useEffect(() => { prewarm(); }, []);

  // Email OR mobile in one field: once it looks like a phone number (digits /
  // spaces / + / -), keep only digits and cap at a 10-digit Indian mobile.
  const onIdentifier = (e) => {
    const v = e.target.value;
    if (/^[\d\s+\-()]*$/.test(v)) {
      setIdentifier(v.replace(/\D/g, "").slice(0, 10));
    } else {
      setIdentifier(v);
    }
  };
  const isMobile = /^\d{10}$/.test(identifier);
  const identifierBad = /^\d+$/.test(identifier) && identifier.length > 0 && identifier.length < 10;

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const slowTimer = setTimeout(() => setSlow(true), 3000);
    try {
      await ensureDbReady();          // wait out the serverless cold start first
      const u = await login(identifier, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "super_admin" ? "/super" : "/school");
    } catch (err) {
      const detail = apiError(err.response?.data?.detail);
      const waking = /waking up|not currently available|resuming/i.test(detail || "");
      toast.error(waking
        ? "The server is taking longer than usual to wake up. Please tap Sign in once more."
        : (detail || "Login failed"));
    } finally {
      clearTimeout(slowTimer);
      setSlow(false);
      setBusy(false);
    }
  };

  return (
    <div className="ep-aurora ep-grid-bg relative flex min-h-screen items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 grid w-full max-w-5xl overflow-hidden rounded-[1.75rem] border border-slate-800/80 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.65)] lg:grid-cols-[1.05fr_1fr]"
      >
        {/* ---------- Left: brand ---------- */}
        <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-white via-white to-indigo-50 p-11 lg:flex">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-indigo-300/30 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-cyan-300/25 blur-3xl"
          />

          <BrandMark size={48} className="relative" />

          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600">
              {L?.login_kicker || "Multi-School ERP"}
            </p>
            <h2 className="mt-3 font-head text-[2.1rem] font-extrabold leading-[1.1] tracking-tight text-slate-900">
              {L?.login_title || "Run every school from one command center."}
            </h2>
            <ul className="mt-6 space-y-2.5">
              {points.map((h) => (
                <li key={h} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-500" />
                  {h}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative flex divide-x divide-slate-200 rounded-2xl border border-slate-200 bg-white/70 backdrop-blur">
            {stats.map((st, i) => (
              <div key={i} className="flex-1 px-4 py-3">
                <p className="font-head text-lg font-bold text-slate-900">{st.value}</p>
                <p className="text-[11px] text-slate-500">{st.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ---------- Right: form ---------- */}
        <div className="ep-glass relative p-8 sm:p-11">
          <div className="mb-7 flex items-center justify-center rounded-2xl bg-white py-3 lg:hidden">
            <BrandMark size={38} />
          </div>

          <h1 className="font-head text-[1.7rem] font-bold tracking-tight text-white">Sign in</h1>
          <p className="mt-1.5 text-sm text-slate-400">Access your school dashboard</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-400">Email or mobile number</span>
              <input
                type="text"
                inputMode="text"
                required
                autoComplete="username"
                autoFocus
                value={identifier}
                onChange={onIdentifier}
                className="ep-input"
                placeholder="you@school.edu  ·  98765 43210"
                data-testid="login-email-input"
              />
              {identifierBad && (
                <span className="mt-1 block text-[11px] text-amber-400" data-testid="login-mobile-hint">
                  Mobile number must be 10 digits ({identifier.length}/10)
                </span>
              )}
              {isMobile && (
                <span className="mt-1 block text-[11px] text-emerald-400">Mobile number ✓</span>
              )}
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-400">Password</span>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="ep-input pr-11"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-500 transition hover:text-slate-200"
                  data-testid="login-password-toggle"
                >
                  {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </label>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-indigo-300 transition hover:text-indigo-200"
                data-testid="login-forgot-link"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={busy || identifierBad}
              className="ep-btn w-full !py-3 text-[0.95rem]"
              data-testid="login-submit-btn"
            >
              {busy
                ? <><Loader2 size={18} className="animate-spin" /> {slow ? "Waking the server…" : "Signing in…"}</>
                : <>Sign in <ArrowRight size={16} /></>}
            </button>

            {slow && (
              <p className="text-center text-xs text-slate-400" data-testid="login-slow-note">
                First sign-in after a quiet spell can take up to a minute — no need to do anything, it will open automatically.
              </p>
            )}
          </form>

          <div className="mt-7 border-t border-slate-700/60 pt-5">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 transition hover:text-slate-300"
              data-testid="login-public-link"
            >
              <Globe size={13} /> Go to public school website
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
